import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import Loading from 'components/global/Loading';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';

import { setSallaStores } from 'pages/SalesChannels/ConnectedStores/Salla.slice';
import useSallaSync from 'pages/SalesChannels/useSallaSync';

import { LoadingOverlay, ModalContentWrapper } from './SallaConnectModal.styled';

const SALLA_AUTH_URL = 'https://s.salla.sa/auth/';

/**
 * Modal for connecting to Salla with a webview (Desktop app only)
 */
const SallaConnectModal = NiceModal.create(() => {
	const modal = useModal();
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(true);
	const { logEcommerceRequest } = useSallaSync();
	const webviewRef = useRef(null);
	const { showSuccess } = useAlert();

	useEffect(() => {
		// Basic loading handler
		const handleWebViewLoad = () => {
			webviewRef.current.executeJavaScript(`localStorage.clear();`).then(() => {
				setIsLoading(false);
			});
		};

		const handleNavigationChange = async () => {
			const webviewElement = webviewRef.current;
			if (!webviewElement) return;
			const currentUrl = webviewElement.getURL();
			if (currentUrl.includes('auth')) {
				webviewElement.executeJavaScript(`localStorage.getItem('user');`).then(async (result) => {
					const auth = JSON.parse(result);

					if (auth && !currentUrl.includes('auth/verification')) {
						const userDataRes = await fetch('https://api.salla.dev/admin/v2/oauth2/user/info', {
							method: 'GET',
							headers: {
								Authorization: `Bearer ${auth.token}`
							}
						});

						if (userDataRes.ok) {
							const { data: userData } = await userDataRes.json();
							if (!userData?.email) return;
							await dispatch(
								setSallaStores({
									...auth,
									...userData.merchant,
									username: userData.name,
									email: userData.email
								})
							).then((storeData) => {
								modal.resolve(storeData.payload);
							});
							logEcommerceRequest({
								isFailure: false,
								url: 'https://api.salla.dev/admin/v2/oauth2/user/info',
								request: JSON.stringify({ token: auth.token }),
								response: JSON.stringify(userData),
								action: 'connectSallaAccount'
							});
							showSuccess();
						} else {
							logEcommerceRequest({
								isFailure: true,
								url: 'https://api.salla.dev/admin/v2/oauth2/user/info',
								request: JSON.stringify({ token: auth.token }),
								response: JSON.stringify(userDataRes),
								action: 'connectSallaAccount'
							});
						}
					}
				});
			} else {
				modal.remove();
			}
		};

		const webviewElement = webviewRef.current;
		if (webviewElement) {
			webviewElement.addEventListener('dom-ready', handleWebViewLoad);
			webviewElement.addEventListener('did-navigate', handleNavigationChange);
			webviewElement.addEventListener('did-navigate-in-page', handleNavigationChange);
			window.electronAPI.changeUserAgent(webviewElement.partition);
		}

		return () => {
			const webviewElement = webviewRef.current;
			if (webviewElement) {
				webviewElement.removeEventListener('did-navigate', handleNavigationChange);
				webviewElement.removeEventListener('did-navigate-in-page', handleNavigationChange);
				webviewElement.removeEventListener('dom-ready', handleWebViewLoad);
			}
		};
	}, [modal]);

	return (
		<Modal position="center" fullwidth maxWidth="large">
			<ModalTitle>
				<Typography variant="p1">connectSallaAccount</Typography>
			</ModalTitle>

			<ModalContentWrapper>
				{isLoading && (
					<LoadingOverlay>
						<Loading />
					</LoadingOverlay>
				)}
				{/* Using Electron's webview tag which can bypass iframe restrictions */}
				<webview
					className="webview"
					ref={webviewRef}
					src={SALLA_AUTH_URL}
					data-allowpopups="true"
					// eslint-disable-next-line react/no-unknown-property
					partition="persist:sallawebview"
				/>
			</ModalContentWrapper>
		</Modal>
	);
});

export default SallaConnectModal;
